import {
    GridApi,
    GridReadyEvent,
    RowClickedEvent,
    ColDef,
    ValueSetterParams,
    RowClassParams,
    RowDoubleClickedEvent,
} from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import { AgGridReact } from '@ag-grid-community/react';
import cn from 'classnames';
import GridToolbar from 'es/app/components/data-grid/GridToolbar';
import { getIn } from 'formik';
import React, { CSSProperties, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'es/app/theme/useTheme';
import { BootstrapStyles } from 'es/app/visuals/styles/BootstrapStyles';
import './Grid.scss';
import { IGridProps } from 'es/app/components/data-grid/Grid.types';
import { useGridColumnDefs } from 'es/app/components/data-grid/useGridColumnDefs';
import { useGridSearch } from 'es/app/components/data-grid/useGridSearch';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import { NestedKeyOf } from 'es/app/types';

export function Grid<TItem extends Record<string, any>>(props: IGridProps<TItem>) {
    const { gridApi, handleGridReady } = useSetGridApi({
        gridApiRef: props.gridApiRef,
        onGridReady: props.onGridReady,
    });
    const { displayedTotal, quickFilterText, onFilterTextChange, onFilterChanged } = useGridSearch({
        data: props.data,
        gridApi,
    });

    const { columns, data, rowId } = useMemo(() => {
        return {
            data: props.isLoading
                ? (Array.from({ length: 30 }).map((_, index) => ({
                      id: String(index),
                  })) as unknown as TItem[])
                : props.data,
            columns: props.columns.map(v => ({
                ...v,
                cellClass: classNames(v.cellClass, {
                    [Classes.SKELETON]: props.isLoading,
                }),
            })),
            rowId: props.isLoading ? ('id' as NestedKeyOf<TItem>) : props.rowId,
        };
    }, [props.columns, props.data, props.isLoading, props.rowId]);

    return (
        <>
            {!props.hideToolbar && (
                <GridToolbar
                    total={props.data.length}
                    displayedTotal={displayedTotal}
                    onFilterTextChange={onFilterTextChange}
                    gridActions={props.gridActions}
                    onAdd={props.onAdd}
                    onRefresh={props.onRefresh}
                />
            )}
            <DataGrid
                {...props}
                rowId={rowId}
                data={data}
                columns={columns}
                gridApi={gridApi}
                handleGridReady={handleGridReady}
                quickFilterText={quickFilterText}
                onFilterChanged={onFilterChanged}
            />
        </>
    );
}

function DataGrid<TItem extends Record<string, any>>(
    props: IGridProps<TItem> & {
        handleGridReady: (event: GridReadyEvent) => void;
        gridApi?: GridApi | null;
        quickFilterText: string;
        onFilterChanged: () => void;
    }
) {
    const {
        gridApi,
        height,
        minHeight,
        rowClass,
        selected,
        rowClassRules,
        defaultColumnDef: defaultCOlumnDefProp,
        quickFilterText,
        rowEnabled,
        handleGridReady,
        onRowClick,
        onRowDoubleClick,
        canEdit,
        onEdit,
        onDataItemChanging,
        onFilterChanged,
    } = props;
    const gridContainerRef = React.createRef<HTMLDivElement>();

    /**
     * This is the default value setter used for updating column values in editable grids.
     */
    const setColumnValue = (params: ValueSetterParams<TItem>) => {
        if (!onDataItemChanging) return false;

        const item = params.data;
        const columnId = params.column.getColId();

        const updateResult = onDataItemChanging(item, columnId, params.newValue, params.oldValue);
        return !!updateResult;
    };
    const defaultColumnDef: ColDef<TItem> = {
        filter: true,
        sortable: true,
        resizable: true,
        valueSetter: setColumnValue,
        ...(defaultCOlumnDefProp || {}),
    };

    const columnDefs = useGridColumnDefs(props);

    const gridStyles: CSSProperties = useMemo(
        () => ({
            ...(typeof height === 'number' ? { height: height } : {}),
            ...(typeof minHeight === 'number' ? { minHeight: minHeight } : {}),
        }),
        [height, minHeight]
    );

    const handleRowClick = () => (event: RowClickedEvent) => {
        onRowClick?.(event.data);
    };

    const handleRowDoubleClick = (event: RowDoubleClickedEvent) => {
        const handler = onRowDoubleClick ?? ((!canEdit || canEdit(event.data)) && onEdit);
        handler && handler(event.data);
    };

    // When the user is editing, listen for clicks outside of the grid container, and if any are detected, then
    // force editing to stop.
    const handleClickOutside = (event: MouseEvent) => {
        if (!('target' in event)) return;
        if (!(event.target instanceof Node)) return;
        if (gridContainerRef && !gridContainerRef.current?.contains(event.target)) {
            gridApi?.stopEditing(false);
        }
    };
    const handleEditingStarted = () => document.addEventListener('mousedown', handleClickOutside);
    const handleEditingStopped = () => document.removeEventListener('mousedown', handleClickOutside);
    const getRowId = useGetRowId(props);

    const getRowClass = (params: RowClassParams<TItem>): string | string[] | undefined => {
        if (!(rowClass || selected)) return;

        if (Array.isArray(rowClass)) return rowClass;

        const item = params.data;

        if (!item) return;

        if (typeof rowClass === 'string') return rowClass;
        if (typeof rowClass === 'function') return rowClass(item);
    };

    const getRowClassRules = () => {
        return {
            'row-disabled': (params: RowClassParams<TItem>) =>
                typeof rowEnabled === 'undefined' ? false : !!params.data && !rowEnabled?.(params.data),
            ...(rowClassRules || {}),
        };
    };

    const { themeName } = useTheme();
    const gridClassName = cn('ag-data-grid', props.className, {
        [BootstrapStyles['flex-fill'] ?? '']: !props.height && !props.minHeight,
        'ag-theme-balham': themeName === 'light',
        'ag-theme-balham-dark': themeName === 'dark',
    });

    useCollapseColumns({
        props,
        gridApi,
    });

    useSelectedRows({
        props,
        gridApi,
    });

    return (
        <div className={gridClassName} style={gridStyles} ref={gridContainerRef}>
            <AgGridReact
                onGridReady={handleGridReady}
                onRowClicked={handleRowClick}
                onRowDoubleClicked={handleRowDoubleClick}
                onCellEditingStarted={handleEditingStarted}
                onCellEditingStopped={handleEditingStopped}
                rowSelection="single"
                enableRangeSelection={false}
                suppressRowClickSelection={true}
                rowData={props.data}
                rowClassRules={getRowClassRules()}
                getRowClass={getRowClass}
                getRowHeight={props.getRowHeight}
                pagination={props.pagination}
                paginationPageSize={props.paginationPageSize}
                columnDefs={columnDefs}
                defaultColDef={defaultColumnDef}
                getRowId={({ data }) => getRowId(data)}
                quickFilterText={quickFilterText}
                onFilterChanged={onFilterChanged}
                rowDragManaged={props.rowDragManaged}
                onRowDragEnd={props.onRowDragEnd}
                colResizeDefault="shift"
                headerHeight={props.headerHeight}
                context={props.context}
                modules={[ClientSideRowModelModule, InfiniteRowModelModule]}
            />
        </div>
    );
}

function useSelectedRows<TItem extends Record<string, any>>({
    gridApi,
    props,
}: {
    gridApi?: GridApi | null;
    props: IGridProps<TItem>;
}) {
    const getRowId = useGetRowId(props);
    useEffect(() => {
        if (!gridApi) return;
        gridApi.forEachNode(node => {
            if (!node.data) return;
            const id = getRowId(node.data);
            node.setSelected(id === props.selected);
        });
    }, [props.selected, gridApi, getRowId, props.data]);
}

function useCollapseColumns<TItem extends Record<string, any>>({
    gridApi,
    props,
}: {
    gridApi?: GridApi | null;
    props: IGridProps<TItem>;
}) {
    const { collapse, columns, defaultColumnDef } = props;
    const { collapse: defaultCollapse } = defaultColumnDef ?? {};

    useEffect(() => {
        const collapsibleColumnIDs = columns.flatMap(c => {
            const { id, field, collapse: collapseColumn } = c;
            const fieldId = id ?? field;
            if (!fieldId) return [];
            const shouldCollapse = collapseColumn ?? defaultCollapse ?? false;
            if (!shouldCollapse) return [];
            return fieldId;
        });

        gridApi?.setColumnsVisible(collapsibleColumnIDs, !collapse);
        gridApi?.sizeColumnsToFit();
    }, [collapse, columns, defaultCollapse, gridApi]);
}

function useSetGridApi({
    gridApiRef,
    onGridReady,
}: {
    gridApiRef?: MutableRefObject<GridApi | null>;
    onGridReady?: (event: GridReadyEvent) => void;
}) {
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const handleGridReady = useCallback(
        (event: GridReadyEvent) => {
            setGridApi(event.api);

            if (gridApiRef) {
                gridApiRef.current = event.api;
            }

            if (onGridReady) {
                onGridReady(event);
            }
        },
        [gridApiRef, onGridReady]
    );
    return {
        handleGridReady,
        gridApi: gridApi ?? gridApiRef?.current,
    };
}

function useGetRowId<TItem extends Record<string, any>>(props: IGridProps<TItem>) {
    const { rowId } = props;
    return useCallback(
        (item: TItem) => {
            if (!rowId) return item;
            return typeof rowId === 'function' ? rowId(item) : getIn(item, rowId);
        },
        [rowId]
    );
}
